import { Component, Ref, Emit, Vue } from 'vue-property-decorator'
import VtBubble from '@/components/common/VtBubble.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { BModal } from 'bootstrap-vue'
import {
    initialShipmentsModalSendState,
    ShipmentsModalSendState
} from '@/modules/shipments-module/components/modal-send/shipments-modal-send.model'
import { GridOptions } from '@ag-grid-enterprise/all-modules'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import {
    ShipmentCreateRequest,
    ShipmentRequestResponse,
    ShipmentResponse
} from '@/modules/shipments-module/services/shipments.dto'
import { CommonLaboratoryResponse } from '@/api/services/common.dto'
import { shipmentsCreatorService } from '@/modules/shipments-module/services/creator/shipments-creator.service'
import { shipmentsReportService } from '@/modules/shipments-module/services/report/shipments-report.service'
import { SHIPMENT_COREENTITY_VALUES } from '@/modules/shipments-module/old/constants/shipmentCoreEntities'
import { uniq } from 'lodash'
import { shipmentEntityState } from '@/api/resources/entityState/shipment-entity-state'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { StrictNullable } from '@/shared/types/app.types'
import { FileUtils } from '@/shared/util/file-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'

@Component({
    components: {
        VtBubble,
        VtGrid
    }
})
export default class ShipmentsModalSend extends Vue {
    @Ref() readonly shipmentsModalSend!: BModal

    @Emit()
    sent(): void {
        // emits an event
    }

    state: ShipmentsModalSendState
    constructor() {
        super()
        this.state = initialShipmentsModalSendState()
    }

    private get _originLaboratory(): StrictNullable<CommonLaboratoryResponse> {
        return this.state.shipments.length
            ? this.state.shipments[0].originLaboratory
            : null
    }
    private get _destinationLaboratory(): StrictNullable<CommonLaboratoryResponse> {
        return this.state.shipments.length
            ? this.state.shipments[0].destinationLaboratory
            : null
    }

    get title(): string {
        if (this.caseNumbers) {
            const choice = this.caseNumbers.length > 1 ? 2 : 1
            return `${this.caseNumbers.length} ${this.$tc('shipments.modal.element', choice).toLowerCase()} ${this.$tc('shipments.modal.toSend')}:`
        }
        return ''
    }

    get destinationLaboratoryLabel(): string {
        return this._destinationLaboratory?.labName ?? ''
    }

    get caseNumbers(): string[] {
        return uniq(this.state.shipments.map(shipment => shipment.casenumber))
    }

    get gridOptions(): GridOptions {
        return {
            sideBar: gridSideBarConfig,
            rowModelType: AgGridRowModelType.CLIENT_SIDE
        }
    }

    /// VUE HOOKS ///

    async mounted(): Promise<void> {
        // load the states needed for the grid entity status column filter
        await PromiseUtils.toCallUnique(shipmentEntityState.loadStates())
    }

    /// SERVICE CALLS ///

    private async _createShipment(laboratoryId: number, request: ShipmentCreateRequest): Promise<StrictNullable<ShipmentResponse>> {
        if (request) {
            const response = await PromiseUtils.toCallUnique(shipmentsCreatorService.create(laboratoryId, request))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('shipments.toastMessages.requestSent'))
                return response
            }
        }
        return null
    }

    private _downloadOrderReport(laboratoryId: number, shipmentId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallUnique(
            shipmentsReportService.getShippingOrderReport(laboratoryId, shipmentId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// MODAL OPERATIONS ///

    private static _getEntityLabel(shipmentRequestResponse: ShipmentRequestResponse): string {
        // Map entity label to make filter work
        switch (shipmentRequestResponse?.entityType.code) {
        case SHIPMENT_COREENTITY_VALUES.SAMPLE.code:
            return shipmentRequestResponse.sample.sampleLabel
        case SHIPMENT_COREENTITY_VALUES.PORTION.code:
            return shipmentRequestResponse.portion.portionLabel
        case SHIPMENT_COREENTITY_VALUES.SUBPORTION.code:
            return shipmentRequestResponse.subportion.subportionLabel
        default:
            return ''
        }
    }

    private _setRowData(): void {
        if (this.caseNumbers?.length && this.state.activeTabIndex !== -1) {
            this.state.rowData =
                this.state.shipments
                    .filter((shipment) =>
                        shipment.casenumber === this.caseNumbers[this.state.activeTabIndex])
                    .map((shipment) =>
                        ({
                            ...shipment,
                            entityLabel: ShipmentsModalSend._getEntityLabel(shipment)
                        }))
        }
    }

    show(selectedRows: ShipmentRequestResponse[]): void {
        this.state = initialShipmentsModalSendState()
        this.state.shipments = selectedRows
        this._setRowData()

        this.shipmentsModalSend.show()
    }
    hide(): void {
        this.shipmentsModalSend.hide()
    }

    onChangeTab(): void {
        this._setRowData()
    }

    /// GRID OPERATIONS ///

    countElements(caseNumber: string): number {
        return this.state.shipments.filter(shipment => shipment.casenumber === caseNumber).length
    }

    async createOperation(): Promise<void> {
        if (this._originLaboratory && this._destinationLaboratory && this.state.shipments.length) {
            const request: ShipmentCreateRequest = {
                destinationLaboratoryId: this._destinationLaboratory.labId,
                shipmentRequestIds: this.state.shipments.map(row => row.id),
                observations: this.state.observations.trim().length ? this.state.observations : undefined
            }
            const response = await this._createShipment(this._originLaboratory.labId, request)
            if (response) {
                const blob = await this._downloadOrderReport(this._originLaboratory.labId, response.id)
                if (blob) {
                    FileUtils.openFile({ blob })

                    this.sent() // emits an event
                    this.hide()
                }
            }
        }
    }
}
